/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { DisplayKey } from '../../Game/DisplayKey';
import StateComponent from '../../StateManager/StateComponent';
import StateDependencyComponent from '../../StateManager/StateDependencyComponent';
import StateEvent from '../../StateManager/StateEvent';
import { StateID } from '../../StateManager/StateID';
import StateReference from '../../StateManager/StateReference';
import ReferencedComponent from '../Component/ComponentReference/ReferencedComponent';
import { ReferenceGroup } from '../Component/ComponentReference/ReferenceGroup';
import ComponentBase from '../ComponentBase';
import CLog, { LogTypes } from '../ComponentLogger';
import Decorators from '../Decorators';
import PrefabDefinition from '../Prefab/PrefabDefinition';

/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

@cc._decorator.ccclass
export default abstract class PrefabLoaderBase extends StateDependencyComponent
{
    @property({ type: StateReference })
    protected loadingState: StateReference = new StateReference(StateID.Loading, true, ReferenceGroup.MainGame);

    @property({ type: StateReference })
    protected cleanupState: StateReference
        = new StateReference(StateID.PostInitializing, true, ReferenceGroup.MainGame);

    @property({ type: [PrefabDefinition], visible: true })
    protected prefabDefinitions: PrefabDefinition[] = [];
    public get PrefabDefinitions(): PrefabDefinition[] { return this.prefabDefinitions; }
    public set PrefabDefinitions(value: PrefabDefinition[]) { this.prefabDefinitions = value; }

    @property
    public get Sort(): boolean { return false; }
    public set Sort(value: boolean) { this.sortDefinitions(); }

    @property({ type: cc.Node })
    public get SetParentNode(): cc.Node { return null; }
    public set SetParentNode(value: cc.Node)
    {
        if (!value) { return; }
        this.prefabDefinitions.forEach(def => def.ParentNode = value);
    }

    @property
    protected prefabFolderName: string = '';

    @property
    public get Rename(): boolean { return false; }
    public set Rename(value: boolean) { this.rename(); }

    private _loadingParallel: boolean = false;
    private _hasLoaded: boolean = false;

    protected optionalMap: Map<DisplayKey, PrefabDefinition[]> = new Map();

    protected onLoad(): void
    {
        super.onLoad();

        this.configureOptional();
        this.loadSerial();
    }

    protected start(): void
    {
        super.start(this.loadingState, this.cleanupState);
    }

    protected update(): void
    {
        if (!this._loadingParallel) { return; }

        this._loadingParallel = this.prefabDefinitions.some(def => !def.IsLoaded);

        if (!this._loadingParallel) { this.completeState(this.loadingState.State); }
    }

    protected cleanup(state: StateComponent): void
    {
        if (!this._hasLoaded) { return; }
        this.node.childrenCount ? this.destroy() : this.node.destroy();
        this.completeState(state);
    }

    protected loadParallel(): void
    {
        this._loadingParallel = true;

        this.prefabDefinitions.forEach(prefabDef => this.loadPrefabDefinition(prefabDef));
    }

    @Decorators.BindThis()
    protected loadSerial(index: number = 0): void
    {
        if (index >= this.prefabDefinitions.length)
        {
            if (this._hasLoaded)
            {
                this.cleanup(this.cleanupState.Value);
            }
            else
            {
                this._hasLoaded = true;
            }
            if (this.loadingState.Value.IsCurrentState()) { this.completeState(this.loadingState.Value); }
            return;
        }

        const prefabDef: PrefabDefinition = this.prefabDefinitions[index];
        this.loadPrefabDefinition(prefabDef, () => this.loadSerial(++index));
    }

    protected onStateChangeHandler = (event: StateEvent) =>
    {
        if (event.State === this.loadingState.State && this._hasLoaded)
        {
            this.completeState(event.State);
        }
        if (event.State === this.cleanupState.State)
        {
            this.loadOptional(event.State);
        }
    }

    protected abstract configureOptional(): void;
    protected abstract loadOptional(event: StateComponent): void;

    private _progress: number = 0;
    public get Progress(): number { return this._progress; }

    private loadPrefabDefinition(prefabDef: PrefabDefinition, callBack: Function = null): void
    {
        this._progress = ((this.prefabDefinitions.indexOf(prefabDef) + 1) / this.prefabDefinitions.length) * 100;
        const safeCallBack: Function = () => callBack && callBack();
        cc.loader.loadRes(prefabDef.PrefabPath, cc.Prefab, (error: Error, prefab: cc.Prefab) =>
            {
                if (error)
                {
                    CLog.error(error.message, this, LogTypes.Utility);
                    prefabDef.IsLoaded = true;
                    safeCallBack();
                    return;
                }

                this.loadPrefab(prefab, prefabDef);
                safeCallBack();
            });
    }

    private loadPrefab(prefab: cc.Prefab, prefabDef: PrefabDefinition): void
    {
        const instance: cc.Node = cc.instantiate(prefab);

        if (prefabDef.ActiveForInjection)
        {
            const component: ReferencedComponent
                = ComponentBase.GetOrAddComponent(instance, ReferencedComponent);

            component.InstanceId = prefabDef.InstanceID;
            component.IsActive = prefabDef.ActiveForInjection;
            component.AddReference();
        }

        // Ensure child components are injected immediately.
        for (const child of instance.getComponentsInChildren(ReferencedComponent))
        {
            child.AddReference();
        }

        const parent: cc.Node = prefabDef.ParentNode ? prefabDef.ParentNode : this.node;
        parent.insertChild(instance, prefabDef.ZOrder);

        instance.active = prefabDef.NodeActive;
        prefabDef.IsLoaded = true;

        cc.loader.releaseRes(prefabDef.PrefabPath);
    }

    private sortDefinitions(): void
    {
        const sortedDefs: PrefabDefinition[] = this.prefabDefinitions
            .slice()
            .sort(PrefabLoaderBase.prefabDefinitionCompare);

        this.prefabDefinitions.length = 0;
        sortedDefs.forEach((def, index) => setTimeout(() => this.prefabDefinitions.push(def), 10 * index));
    }

    private rename(): void
    {
        this.prefabFolderName = this.prefabFolderName.trim();
        if (!this.prefabFolderName) { return; }

        this.prefabDefinitions.forEach(def =>
            {
                if (def.PrefabPath.startsWith('Playbar')) { return; }

                const path: string[] = def.PrefabPath.split('/');
                path[0] = this.prefabFolderName;
                def.PrefabPath = path.join('/');
            });

        this.prefabFolderName = '';
    }

    protected static prefabDefinitionCompare(a: PrefabDefinition, b: PrefabDefinition): number
    {
        // null case
        if (a && !b) { return -1; }
        if (!a && b) { return 1; }
        if (!a && !b) { return 0; }

        // has parent
        if (a.ParentNode && !b.ParentNode) { return -1; }
        if (!a.ParentNode && b.ParentNode) { return 1; }

        let currentCompare: number = 0;

        // parent name
        if (a.ParentNode && b.ParentNode)
        {
            currentCompare = a.ParentNode.name.localeCompare(b.ParentNode.name);
            if (currentCompare !== 0) { return currentCompare; }
        }

        // z-Order
        currentCompare = a.ZOrder - b.ZOrder;
        if (currentCompare !== 0) { return currentCompare; }

        // prefab path
        return a.PrefabPath.localeCompare(b.PrefabPath);
    }
}
